import { Injectable } from '@angular/core';
import { Environment } from '@rma/generic/util/environment';
import { ArticleMetaOptions, MetaTag, NameMetaTag, NamePropertyMetaTag, PropertyMetaTag, SocialTagConfig } from '../domain';
import { SocialMetaTagsService } from '../util-social-meta-tags-service/social-meta-tags.service';

export interface MetaTagData {
  title: string;
  description: string;
  image: string;
  url: string;
  author?: Partial<ArticleMetaOptions> | null;
  keywords?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class MetaTagService {
  constructor(
    private readonly environment: Environment,
    private readonly socialMetaTagsService: SocialMetaTagsService,
  ) {}

  public generateMetaTags(tagData: MetaTagData, social: Partial<SocialTagConfig>): Array<NameMetaTag | PropertyMetaTag> {
    const { author, keywords, ...socialData } = tagData;

    const socialMeta = this.socialMetaTagsService.getSocialMetaDataTags(social, socialData);
    const defaultMeta = this.getDefaultSiteMetaTags(socialData.image, tagData.description);

    const articleMeta = author === null ? [] : this.getArticleMeta(author);
    const keywordsMeta = keywords ? [this.getKeywordsMeta(keywords.join(', '))] : [];

    return [...defaultMeta, ...socialMeta, ...articleMeta, ...keywordsMeta];
  }

  private getDefaultSiteMetaTags(image: string, description: string): Array<MetaTag> {
    const { facebookId, twitterName } = this.environment.social;
    const locale = this.environment.language.locale;
    return [
      this.getDescriptionMeta(description),
      this.propertyMeta('og:site_name', this.environment.domain),
      this.propertyMeta('og:locale', locale),
      this.propertyMeta('fb:app_id', facebookId),
      this.namedMeta('twitter:site', twitterName),
      this.namedMeta('twitter:site', twitterName),
      this.namedMeta('twitter:creator', twitterName),
      this.propertyMeta('og:image', image),
    ];
  }

  private getKeywordsMeta(content: string): NameMetaTag {
    return this.namedMeta('keywords', content);
  }

  private getDescriptionMeta(content: string): NameMetaTag {
    return this.namePropertyMeta('description', 'og:description', content);
  }

  private getArticleMeta(content?: Partial<{ author: string; publisher: string }>): PropertyMetaTag[] {
    return [
      this.propertyMeta('article:author', content?.author ?? this.environment.baseUrl),
      this.propertyMeta('article:publisher', content?.publisher ?? this.environment.baseUrl),
    ];
  }

  private namedMeta(name: string, content: string): NameMetaTag {
    return { name, content };
  }

  private propertyMeta(property: string, content: string): PropertyMetaTag {
    return { property, content };
  }

  private namePropertyMeta(name: string, property: string, content: string): NamePropertyMetaTag {
    return { name, property, content };
  }
}
